import { useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '@lib/api/client';

// ── Self-serve password reset (portal audit: the only recovery path was
// phoning Erik so he could manually issue a reset invite) ────────────────────

export const PortalForgotPage = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/portal/auth/forgot', { email: email.trim() });
      setSent(true);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error || 'Something went wrong — try again in a minute.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/icon-192.png" alt="OchrePoint" className="w-16 h-16 rounded-2xl mx-auto mb-3 shadow-sm" />
          <h1 className="text-2xl font-bold text-[#2C1810]">Reset your password</h1>
          <p className="text-gray-500 text-sm mt-2">We'll email you a secure link to set a new one.</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          {sent ? (
            <div className="text-center space-y-3 py-2">
              <span className="material-symbols-outlined text-green-500 text-5xl">mark_email_read</span>
              <p className="text-sm font-semibold text-gray-900">Check your inbox</p>
              <p className="text-xs text-gray-500">
                If <strong>{email}</strong> has a portal account, a reset link is on its way.
                It works once and expires in 24 hours.
              </p>
              <Link to="/portal/login" className="inline-block mt-2 text-sm font-semibold text-[#C77D3F]">← Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <p className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</p>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                  autoComplete="email" placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
              </div>
              <button type="submit" disabled={busy || !email.trim()}
                className="w-full bg-[#C77D3F] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50">
                {busy ? 'Sending…' : 'Email me a reset link'}
              </button>
              <p className="text-center">
                <Link to="/portal/login" className="text-xs text-gray-400 hover:text-[#C77D3F]">← Back to sign in</Link>
              </p>
            </form>
          )}
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-6">
          Locked out completely? Call us at <a href="tel:+12505100677" className="text-[#C77D3F]">(250) 510-0677</a>
        </p>
      </div>
    </div>
  );
};
